import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves this to a served URL for the pdf.js web worker (ESM build).
// pdf.js runs its parser off the main thread; without a workerSrc it throws.
import PdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import {
  EMPTY_GUID,
  isLayoutBlockType,
  stepsWithPdfTemplates,
  type PdfFieldMapping,
  type WorkflowField,
  type WorkflowFieldType,
  type WorkflowTemplate,
} from '@/api/workflows';

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfWorkerUrl;

export { pdfjsLib };

/**
 * Fields common to every mappable row — the full column path so the save
 * endpoint can address it plus the breadcrumb label.
 */
type MappableBase = {
  /**
   * Unique per row. `fieldId` for a scalar field; `fieldId:columnId:rowIndex` for a
   * repeatable-row column; a `#instanceIndex` suffix is added for fields/options that
   * live in a repeatable section.
   */
  key: string;
  fieldId: string;
  stepId: string;
  sectionId: string;
  rowId: string;
  columnId: string;
  /** Breadcrumb label (step › section › field[ › column]) in the active language. */
  label: string;
  /**
   * The breadcrumb, pre-split. The mapping page groups by step and then section
   * and prints only {@link leafLabel} on the row itself, so the same text is not
   * repeated on all 500-odd rows — splitting `label` on the separator would work
   * until a field label legitimately contains one.
   */
  stepLabel: string;
  sectionLabel: string;
  /** Just this row's own name: the field, plus its option or column when it has one. */
  leafLabel: string;
  /**
   * When this row belongs to a repeatable group (a RepeatableRow field, or a field
   * inside a repeatable section), the stable id of that group — the mapping page folds
   * all rows sharing a `groupKey` under one collapsible header. Undefined for
   * standalone (non-repeatable) rows, which render exactly as before.
   */
  groupKey?: string;
  /** Breadcrumb header shown on the collapsible group (step › section[ › field]). */
  groupLabel?: string;
  /** 1-based index within the group (repeatable-section instance, or repeatable row). */
  instanceIndex?: number;
};

/** No hard limit on a repeatable element → cap the generated rows at this many. */
export const DEFAULT_REPEATABLE_LIMIT = 10;

/**
 * One row in the PDF-mapping page's "form fields" list. A scalar field carries a
 * list of per-PDF `mappings` (1-to-many across documents); a choice option row
 * carries the same list but toggles a checkbox mapping keyed by its option value;
 * a RepeatableRow column carries `columnMappings`, which are scoped to a PDF
 * entry in the same way — one row of one column can fill a box in each document
 * independently.
 */
export type MappableField =
  | (MappableBase & {
      kind: 'field';
      mappings: PdfFieldMapping[];
      /** Source field's type + validate rule — the two signals the segment
       *  picker uses to offer date parts vs SIN / phone / postal presets. */
      fieldType: WorkflowField['type'];
      validate?: string | null;
      /** Whether the source field carries options, so the value-source picker
       *  knows it can offer "option label". Not the options themselves — the
       *  picker only needs to know they exist. */
      hasOptions: boolean;
      /** The option values and their English labels, for the "fires only on"
       *  gate picker. Unlike {@link hasOptions}, this one DOES need the values:
       *  the gate stores an option value, and an originator picking it from a
       *  list cannot typo a gate that could never fire. Empty on a field with
       *  no options. */
      optionChoices: { value: string; label: string }[];
    })
  | (MappableBase & {
      kind: 'option';
      /** The option's value within the parent choice field. */
      optionValue: string;
      mappings: PdfFieldMapping[];
    })
  | (MappableBase & {
      kind: 'column';
      /** Addresses one column inside the parent RepeatableRow's template. */
      repeatableColumnId: string;
      /** 1-based repeatable row this entry maps (each row maps independently). */
      rowIndex: number;
      /** The row's WHOLE-VALUE handle IN THE SELECTED DOCUMENT, or null when it
       *  has none. Clicking a box in the viewer sets exactly this one, so the
       *  click-to-map behaviour is unchanged by segments. */
      internalName: string | null;
      /** The entry {@link internalName} was actually found on — the selected one,
       *  or `EMPTY_GUID` when what matched was a legacy all-documents handle.
       *  Null when there is no handle.
       *
       *  Needed because clearing keys on the entry: sending the selected id to
       *  clear a legacy handle matches nothing and silently does nothing, while
       *  the UI reports success. */
      internalNameEntryId: string | null;
      /** Every handle this row carries — one when the cell fills a single box,
       *  several when its parts go to separate boxes (a date cell feeding year /
       *  month / day combs). Each carries the part it writes, and the document it
       *  writes into. Empty = unmapped.
       *
       *  Handles for EVERY document are listed, not just the selected one, so a
       *  row reads the same way a scalar field's tag list does — the chip names
       *  its entry. */
      columnMappings: {
        internalName: string;
        /** WHICH document this handle fills. `EMPTY_GUID` is the legacy "every
         *  document" sentinel, rendered as such on the chip. */
        pdfEntryId: string;
        segment?: string | null;
        /** Separator with which this handle APPENDS to its box instead of
         *  replacing it, so two columns can share one box. Null = replace. */
        joinWith?: string | null;
        /** True when another column of the same table fills this same box on
         *  this row — the only situation where a join means anything, and so
         *  the only one where the panel offers the control. */
        joinable?: boolean;
      }[];
      /** The COLUMN's type + validate rule, so the segment picker can offer the
       *  same date / SIN / phone / postal presets it offers on a scalar field. */
      columnType: WorkflowFieldType;
      columnValidate?: string | null;
      /**
       * Set only on a CHOICE column, where the row is one OPTION of that column
       * rather than the column itself. The PDF renders such a choice as a set of
       * checkbox widgets — the RDSP PAC frequency row is one AcroForm field
       * carrying eight of them — so each option needs its own handle
       * (`Check Box4.01c:W`). Undefined on an ordinary scalar column, which keeps
       * exactly one handle per row.
       */
      optionValue?: string;
      /**
       * Set only on a DERIVED row, where the value belongs to the whole column
       * rather than to any one of its cells — `total` is the column's cross-row
       * sum, which the advisor never types and which `sumValidation` only ever
       * validated. Undefined on an ordinary row, which maps its own cell.
       *
       * Part of the handle's identity at the endpoint, so it must be sent on a
       * clear as well as on an add: a total is stored under `rowIndex: 1` and
       * would otherwise collide with row 1's own cell handle.
       */
      source?: 'total';
    })
  | (MappableBase & {
      /**
       * A COUNTER: how many rows / instances a repeatable holds. Not a field —
       * nobody types it, it has no builder control, and neither the advisor nor
       * the approver's form preview ever sees it. It exists in exactly two
       * places: this list, and the generated PDF.
       *
       * Its own kind rather than a flagged `field` because every per-chip
       * control the page offers — segment, value source, addendum — is gated on
       * `kind === 'field'`, and none of them means anything on a one- or
       * two-digit integer that lands in one box whole.
       */
      kind: 'count';
      /**
       * WHICH repeatable is counted, and so which endpoint the row saves
       * through. `rows` stores on the parent RepeatableRow field's own
       * `pdfMappings` (dead storage otherwise — the field has no scalar value);
       * `instances` stores on the section's `countPdfMappings`.
       */
      countOf: 'rows' | 'instances';
      mappings: PdfFieldMapping[];
    });

const localized = (en: string, fr: string, isFr: boolean) => (isFr ? fr : en) || en || fr;

/**
 * Walk every DynamicForm step and flatten it into mappable rows. Mirrors the
 * steps → sections → rows → columns → field walk used by
 * {@link collectFieldChoices}, but keeps the full column path (needed to address
 * the save endpoint), skips layout blocks (nothing to map), and expands
 * RepeatableRow fields into one row per template column instead of the parent.
 */
/**
 * Key identifying one AcroForm box at one repeatable row IN ONE DOCUMENT, for
 * spotting boxes that TWO columns of the same table fill — the only place a join
 * is offered.
 *
 * The entry is part of the key because a box only exists inside a document: two
 * columns writing `fundname1` in different PDFs are not sharing anything, and
 * offering them a separator would let an originator author a join that silently
 * never joins.
 *
 * A function rather than an inline template literal on purpose. This was
 * originally written twice, once to build the set and once to query it, and a
 * stray NUL crept into one of the two separators. Both sides looked right in
 * review and in a diff, the set and the lookup simply never matched, and the
 * control silently never rendered. One definition cannot drift from itself.
 */
function sharedBoxKey(pdfEntryId: string, rowIndex: number, acroName: string): string {
  return `${pdfEntryId}|${rowIndex}|${acroName}`;
}

/**
 * Does a column handle belong to the document the originator has open? The
 * authoring-side twin of `collectFilledFields`'s `columnAppliesTo`, and the two
 * must agree: a handle this says is "here" is a handle that fill time will write
 * into this PDF.
 *
 * Absent / `EMPTY_GUID` is the legacy "every document" shape, so a pre-scoping
 * handle still reads as mapped in whichever PDF is on screen — which is exactly
 * what it does at fill time.
 */
function appliesToEntry(mappingEntryId: string | null | undefined, selected: string | null) {
  return !mappingEntryId || mappingEntryId === EMPTY_GUID || mappingEntryId === selected;
}

/**
 * The SLOT one repeatable-column row addresses at the internal-name endpoint —
 * everything that identifies the handle, with neither the box name nor the
 * segment, which are what each call varies.
 *
 * Exists because the slot is the mapping's IDENTITY in the domain: the endpoint
 * matches on every one of these together, so a call that leaves one out
 * addresses a different handle than the row the originator clicked — it clears
 * nothing, or worse, clears the wrong thing. Three call sites build this body
 * (map a box, re-part a chip, remove a chip) and each one got it slightly wrong
 * at least once; `source` is simply the newest member, and the one a total
 * cannot survive without, since a total borrows row 1's index.
 *
 * @param entryId The document the handle belongs to — the selected one when
 *   adding, the chip's own when editing or clearing a handle that may sit on
 *   another document or in the legacy all-documents slot. Null before a document
 *   is chosen, which the endpoint reads as the legacy all-documents slot.
 */
export function columnSlot(
  field: Extract<MappableField, { kind: 'column' }>,
  entryId: string | null,
): {
  repeatableColumnId: string;
  rowIndex: number;
  optionValue: string | null;
  pdfEntryId: string | null;
  source: 'total' | null;
} {
  return {
    repeatableColumnId: field.repeatableColumnId,
    rowIndex: field.rowIndex,
    optionValue: field.optionValue ?? null,
    pdfEntryId: entryId,
    source: field.source ?? null,
  };
}

/**
 * A choice column's handle for one option at one row, in the selected document,
 * paired with the entry it was actually found on.
 *
 * The two travel together because a clear keys on both: reporting a legacy
 * all-documents handle as this row's handle but then clearing it with the
 * SELECTED entry matches nothing, and the page would announce a success that
 * removed nothing. Preferring an exact entry match over a legacy one also means
 * an originator who has re-mapped an option on this document sees that handle
 * rather than the stale shared one.
 */
function optionHandle(
  col: NonNullable<WorkflowField['repeatableTemplate']>[number],
  rowIndex: number,
  optionValue: string,
  selectedPdfId: string | null,
): { internalName: string | null; internalNameEntryId: string | null } {
  const candidates = (col.mappings ?? []).filter(
    (m) =>
      m.rowIndex === rowIndex &&
      m.optionValue === optionValue &&
      appliesToEntry(m.pdfEntryId, selectedPdfId),
  );
  const hit =
    candidates.find((m) => m.pdfEntryId && m.pdfEntryId === selectedPdfId) ?? candidates[0];
  if (!hit) return { internalName: null, internalNameEntryId: null };
  return { internalName: hit.internalName, internalNameEntryId: hit.pdfEntryId ?? EMPTY_GUID };
}

/**
 * An ordinary column's whole-value handle at one row, in the selected document,
 * paired with the entry it was found on — {@link optionHandle}'s twin, and it
 * pairs the two for the same reason.
 *
 * Falls back to the pre-per-row `col.internalName`, which can only ever address
 * row 1 and carries no entry, so it reports `EMPTY_GUID`.
 *
 * `!m.source` is what keeps a DERIVED handle out: a column total is stored under
 * `rowIndex: 1` because the endpoint requires an index, so without this test it
 * reads as row 1's own handle — and clicking row 1 would clear the total.
 */
function wholeValueHandle(
  col: NonNullable<WorkflowField['repeatableTemplate']>[number],
  rowIndex: number,
  selectedPdfId: string | null,
): { internalName: string | null; internalNameEntryId: string | null } {
  const candidates = (col.mappings ?? []).filter(
    (m) =>
      m.rowIndex === rowIndex &&
      !m.optionValue &&
      !m.segment &&
      !m.source &&
      appliesToEntry(m.pdfEntryId, selectedPdfId),
  );
  const hit =
    candidates.find((m) => m.pdfEntryId && m.pdfEntryId === selectedPdfId) ?? candidates[0];
  if (hit) {
    return { internalName: hit.internalName, internalNameEntryId: hit.pdfEntryId ?? EMPTY_GUID };
  }
  const legacy = rowIndex === 1 ? (col.internalName ?? null) : null;
  return { internalName: legacy, internalNameEntryId: legacy ? EMPTY_GUID : null };
}

/**
 * A DERIVED handle — one whose value belongs to the whole column — in the
 * selected document, paired with the entry it was found on. The third sibling of
 * {@link optionHandle} and {@link wholeValueHandle}, and it pairs the two for the
 * same reason they do.
 *
 * Deliberately not folded into `wholeValueHandle`: that one falls back to the
 * pre-per-row `col.internalName`, which is by definition a row-1 CELL handle, and
 * inheriting it here would report a total as mapped to a box that prints a cell.
 */
function derivedHandle(
  col: NonNullable<WorkflowField['repeatableTemplate']>[number],
  source: 'total',
  selectedPdfId: string | null,
): { internalName: string | null; internalNameEntryId: string | null } {
  const candidates = (col.mappings ?? []).filter(
    (m) => m.source === source && appliesToEntry(m.pdfEntryId, selectedPdfId),
  );
  const hit =
    candidates.find((m) => m.pdfEntryId && m.pdfEntryId === selectedPdfId) ?? candidates[0];
  if (!hit) return { internalName: null, internalNameEntryId: null };
  return { internalName: hit.internalName, internalNameEntryId: hit.pdfEntryId ?? EMPTY_GUID };
}

/**
 * @param selectedPdfId The document the originator currently has open, or null
 *   before one is chosen. Only the click-to-map handles (`internalName`) narrow
 *   to it — a click sets a handle on the document on screen. The full
 *   `columnMappings` list keeps every document's handles, so the left-hand list
 *   shows a column's whole mapping picture exactly as it does for a scalar field.
 */
export function collectMappableFields(
  template: WorkflowTemplate,
  isFr: boolean,
  selectedPdfId: string | null = null,
): MappableField[] {
  const out: MappableField[] = [];
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const section of step.sections) {
      const sectionLabel = localized(section.titleEn, section.titleFr, isFr);
      // A repeatable section expands each of its fields/options into one
      // independently-mappable row per instance (1..limit); a non-repeatable
      // section keeps a single row per field, exactly as before.
      const sectionRepeatable = section.repeatable === true;
      const sectionLimit = sectionRepeatable
        ? (section.maxInstances ?? DEFAULT_REPEATABLE_LIMIT)
        : 1;
      const instanceIndices: (number | undefined)[] = sectionRepeatable
        ? Array.from({ length: sectionLimit }, (_, i) => i + 1)
        : [undefined];
      const sectionGroupKey = sectionRepeatable ? section.id : undefined;
      const sectionGroupLabel = sectionRepeatable ? `${stepLabel} › ${sectionLabel}` : undefined;

      // One COUNT row per repeatable section: the box that prints HOW MANY
      // instances it holds. Pushed ahead of the instance rows because it is a
      // fact about the section rather than about any one of them — the same
      // reason it carries no `instanceIndex` and so never renders as "Item N".
      //
      // Addressed by (step, section) alone: there is no field under it, which
      // is exactly why the handles live on the section. `fieldId` carries the
      // section id to keep the row's identity unique and stable in the maps the
      // page keys by it.
      if (sectionRepeatable) {
        out.push({
          fieldId: section.id,
          stepId: step.id,
          sectionId: section.id,
          rowId: '',
          columnId: '',
          stepLabel,
          sectionLabel,
          leafLabel: sectionLabel,
          kind: 'count',
          countOf: 'instances',
          key: `${section.id}:count`,
          groupKey: sectionGroupKey,
          groupLabel: sectionGroupLabel,
          instanceIndex: undefined,
          label: `${stepLabel} › ${sectionLabel}`,
          mappings: section.countPdfMappings ?? [],
        });
      }

      for (const row of section.rows) {
        for (const column of row.columns) {
          const f = column.field;
          if (!f) continue;
          if (isLayoutBlockType(f.type)) continue;
          const fieldLabel = localized(f.labelEn, f.labelFr, isFr);
          const base = {
            fieldId: f.id,
            stepId: step.id,
            sectionId: section.id,
            rowId: row.id,
            columnId: column.id,
          };

          if (
            f.type === 'RepeatableRow' &&
            f.repeatableTemplate &&
            f.repeatableTemplate.length > 0
          ) {
            // The parent row itself isn't an input — each column carries its own
            // AcroForm handles. Rather than an implicit {N} iterator, expand one
            // independently-mappable row per index up to the limit. Not multiplied
            // by a repeatable-SECTION instance: a column handle is keyed on
            // (entry, rowIndex) and carries no instanceIndex, unlike a scalar
            // field's mapping.
            const rowLimit = f.maxRows ?? DEFAULT_REPEATABLE_LIMIT;
            const groupLabel = `${stepLabel} › ${sectionLabel} › ${fieldLabel}`;
            // Boxes that MORE THAN ONE column of this table targets on the same
            // row OF THE SAME DOCUMENT. Joining is only meaningful there —
            // offering the control on every handle would put a separator picker
            // on hundreds of chips that can never use one, and two columns
            // filling the same name in DIFFERENT PDFs never meet at all.
            // Keyed `pdfEntryId + rowIndex + boxName`.
            const sharedBoxes = new Set<string>();
            {
              const seen = new Set<string>();
              for (const c of f.repeatableTemplate)
                for (const m of c.mappings ?? []) {
                  const handle = m.internalName?.trim();
                  // A derived handle shares nothing with a cell: a column total
                  // is one number for the whole column, so "two values meeting
                  // in one box" — the only thing a join can resolve — cannot
                  // arise. Counting it would also make it collide with row 1,
                  // whose index it borrows.
                  if (!handle || m.optionValue || m.source) continue;
                  const key = sharedBoxKey(m.pdfEntryId ?? EMPTY_GUID, m.rowIndex, handle);
                  if (seen.has(key)) sharedBoxes.add(key);
                  else seen.add(key);
                }
            }
            for (const col of f.repeatableTemplate) {
              const colLabel = localized(col.labelEn, col.labelFr, isFr);
              // A CHOICE column needs one target per option per row, not one per
              // row. Its PDF counterpart is a set of checkbox widgets (the RDSP
              // PAC frequency row is one field carrying eight), so a single
              // handle per row left every option after the first with nothing to
              // map to. An ordinary column is unchanged: one target per row.
              const choiceOptions =
                (col.type === 'SingleSelect' ||
                  col.type === 'RadioGroup' ||
                  col.type === 'CheckboxGroup') &&
                col.options?.length
                  ? col.options
                  : null;
              for (let i = 1; i <= rowLimit; i++) {
                if (choiceOptions) {
                  for (const opt of choiceOptions) {
                    const optLabel = localized(opt.labelEn, opt.labelFr, isFr) || opt.value;
                    out.push({
                      ...base,
                      stepLabel,
                      sectionLabel,
                      leafLabel: `${colLabel} › ${optLabel}`,
                      kind: 'column',
                      key: `${f.id}:${col.id}:${i}:${opt.value}`,
                      repeatableColumnId: col.id,
                      rowIndex: i,
                      optionValue: opt.value,
                      groupKey: f.id,
                      groupLabel,
                      instanceIndex: i,
                      label: `${groupLabel} › ${colLabel} › ${optLabel}`,
                      // Backs click-to-map, which is about the document on
                      // screen — so it reads only this entry's handle (or a
                      // legacy all-documents one). Without the entry test a
                      // handle authored on another PDF made this option read as
                      // already mapped here, and clicking cleared THAT handle.
                      ...optionHandle(col, i, opt.value, selectedPdfId),
                      // A per-option handle ticks a checkbox, so it never slices
                      // a value — no picker, no list.
                      columnMappings: [],
                      columnType: col.type,
                      columnValidate: col.validate,
                    });
                  }
                  continue;
                }
                out.push({
                  ...base,
                  stepLabel,
                  sectionLabel,
                  leafLabel: colLabel,
                  kind: 'column',
                  key: `${f.id}:${col.id}:${i}`,
                  repeatableColumnId: col.id,
                  rowIndex: i,
                  groupKey: f.id,
                  groupLabel,
                  instanceIndex: i,
                  label: `${groupLabel} › ${colLabel}`,
                  // EVERY document's handles, each carrying its entry so the chip
                  // can name it — the same picture a scalar field's tag list
                  // gives. Narrowing to the selected PDF is the page's job.
                  columnMappings: (col.mappings ?? [])
                    .filter(
                      (m) =>
                        m.rowIndex === i &&
                        !m.optionValue &&
                        // A derived handle belongs to the column's own row below,
                        // not to row 1 — whose index it borrows only because the
                        // endpoint requires one. Before this test the RIF fund
                        // table's total chipped onto Item 1, and removing it there
                        // sent no source, so the clear matched nothing.
                        !m.source &&
                        m.internalName?.trim(),
                    )
                    .map((m) => ({
                      internalName: m.internalName.trim(),
                      pdfEntryId: m.pdfEntryId ?? EMPTY_GUID,
                      segment: m.segment,
                      joinWith: m.joinWith,
                      // Another column of this table fills the same box on this
                      // row OF THIS DOCUMENT, so appending rather than
                      // overwriting is a real choice the originator can make here.
                      joinable: sharedBoxes.has(
                        sharedBoxKey(m.pdfEntryId ?? EMPTY_GUID, i, m.internalName.trim()),
                      ),
                    })),
                  columnType: col.type,
                  columnValidate: col.validate,
                  // The whole-value handle a viewer click toggles — this
                  // document's, for the same reason as the option branch above.
                  ...wholeValueHandle(col, i, selectedPdfId),
                });
              }
              // One extra target per NUMBER column for the column's cross-row
              // TOTAL, which belongs to no row and so could not be reached by the
              // per-row targets above. The runtime has filled it since
              // `collectColumnDerivedFills` landed — the RIF fund table prints a
              // total under its Amount column — but only an API call could author
              // the handle, because this list had nowhere to put it.
              //
              // `Number` is the gate the back end already uses for `unitToggle`
              // ("only Number columns may carry a toggle"), and a sum of text
              // cells is not a thing an originator means to print.
              if (col.type === 'Number') {
                out.push({
                  ...base,
                  stepLabel,
                  sectionLabel,
                  // The column's own name; the page appends the word "Total", the
                  // way it already prefixes "Item N" on a grouped row — copy
                  // belongs to the page, structure to this module.
                  leafLabel: colLabel,
                  kind: 'column',
                  key: `${f.id}:${col.id}:total`,
                  repeatableColumnId: col.id,
                  // The index the endpoint stores. Meaningless for a total, which
                  // belongs to the whole column, but required — read it as
                  // ignored wherever `source` is set.
                  rowIndex: 1,
                  source: 'total',
                  groupKey: f.id,
                  groupLabel,
                  // NOT a row, so it must not render as "Item 1 · …". Leaving
                  // this unset is what keeps it out of the numbered sequence.
                  instanceIndex: undefined,
                  label: `${groupLabel} › ${colLabel}`,
                  columnMappings: (col.mappings ?? [])
                    .filter((m) => m.source === 'total' && m.internalName?.trim())
                    .map((m) => ({
                      internalName: m.internalName.trim(),
                      pdfEntryId: m.pdfEntryId ?? EMPTY_GUID,
                      segment: m.segment,
                      joinWith: m.joinWith,
                      // A sum is one value in one box — nothing to join with, and
                      // nothing to slice. Both controls stay off this chip.
                      joinable: false,
                    })),
                  columnType: col.type,
                  columnValidate: col.validate,
                  ...derivedHandle(col, 'total', selectedPdfId),
                });
              }
            }
            // One COUNT row for the whole TABLE — how many rows the advisor
            // filled, for a "number of funds" box. Belongs to no column, so it
            // sits outside the loop above, and to no row, so it carries no
            // `instanceIndex`.
            //
            // Its handles live on the parent field's own `pdfMappings`, which
            // is otherwise dead storage: `collectFieldFills` hands a
            // RepeatableRow straight to the row filler without ever reading
            // them, so a field-level mapping on one of these can only ever have
            // meant this. That also means it saves through the ordinary
            // `togglePdfMapping` endpoint with no new wiring, and survives a
            // field property edit — which a column handle does not.
            out.push({
              ...base,
              stepLabel,
              sectionLabel,
              leafLabel: fieldLabel,
              kind: 'count',
              countOf: 'rows',
              key: `${f.id}:count`,
              groupKey: f.id,
              groupLabel,
              instanceIndex: undefined,
              label: groupLabel,
              mappings: f.pdfMappings ?? [],
            });
            continue;
          }

          for (const inst of instanceIndices) {
            const suffix = inst === undefined ? '' : `#${inst}`;
            out.push({
              ...base,
              stepLabel,
              sectionLabel,
              leafLabel: fieldLabel,
              kind: 'field',
              fieldType: f.type,
              validate: f.validate,
              hasOptions: (f.options?.length ?? 0) > 0,
              optionChoices: (f.options ?? []).map((o) => ({
                value: o.value,
                label: o.labelEn || o.value,
              })),
              key: `${f.id}${suffix}`,
              groupKey: sectionGroupKey,
              groupLabel: sectionGroupLabel,
              instanceIndex: inst,
              label: `${stepLabel} › ${sectionLabel} › ${fieldLabel}`,
              mappings: (f.pdfMappings ?? []).filter(
                (m) => (m.instanceIndex ?? undefined) === inst,
              ),
            });
            // Choice types additionally expose each option as its own mappable row
            // — an option maps to a PDF checkbox widget that's CHECKED when picked.
            if (
              f.type === 'SingleSelect' ||
              f.type === 'RadioGroup' ||
              f.type === 'CheckboxGroup'
            ) {
              for (const opt of f.options ?? []) {
                const optLabel = localized(opt.labelEn, opt.labelFr, isFr) || opt.value;
                out.push({
                  ...base,
                  stepLabel,
                  sectionLabel,
                  // Just the option — the row is indented directly beneath its
                  // parent field's row, which already names it.
                  leafLabel: optLabel,
                  kind: 'option',
                  key: `${f.id}::opt::${opt.value}${suffix}`,
                  optionValue: opt.value,
                  groupKey: sectionGroupKey,
                  groupLabel: sectionGroupLabel,
                  instanceIndex: inst,
                  label: `${stepLabel} › ${sectionLabel} › ${fieldLabel} › ${optLabel}`,
                  mappings: (opt.pdfMappings ?? []).filter(
                    (m) => (m.instanceIndex ?? undefined) === inst,
                  ),
                });
              }
            }
          }
        }
      }
    }
  }
  return out;
}

/** Flatten every PDF source template into a picker list for the viewer. */
export type PdfEntryOption = {
  id: string;
  label: string;
  enFileKey: string | null;
  frFileKey: string | null;
};

export function collectPdfEntryOptions(template: WorkflowTemplate): PdfEntryOption[] {
  const out: PdfEntryOption[] = [];
  for (const step of stepsWithPdfTemplates(template)) {
    for (const e of step.fileEntries) {
      out.push({
        id: e.id,
        label: e.label || e.id,
        enFileKey: e.enFileKey ?? null,
        frFileKey: e.frFileKey ?? null,
      });
    }
  }
  return out;
}
